import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Trophy,
  Share2,
  Loader2,
  MapPin,
  Clock,
  Footprints,
  ArrowRight,
  Camera,
  Globe,
  LinkIcon,
  ImageIcon,
  X,
} from 'lucide-react';
import { partnerAPI, projectAPI, createQueryKey } from '../services/api';
import { getImageUrl } from '../config/runtime';
import {
  buildCanonicalProjectShareUrl,
  buildProjectShareClipboardText,
  copyTextToClipboard,
} from '../utils/projectShare';

/**
 * End-of-tour souvenir (plan_real_guide_gaps.md workstream S).
 *
 * Shown on the project overview once every objective is checked off. The hero
 * is deliberately compact (the page hero above already shows the photo grid)
 * and instead previews the actual postcard, so "Share postcard" is never a
 * black box. The postcard is personal: the visitor's best photo, THEIR route
 * drawn as a stylized sketch (stamp-style — no map tiles, no CORS taint),
 * their completion date and photo count, plus venue branding.
 */

const WALK_SPEED_M_PER_MIN = 75;
const DWELL_MINUTES_PER_STOP = 5;
// Streets aren't straight lines — matches backend ROUTE_DETOUR_FACTOR
const ROUTE_DETOUR_FACTOR = 1.25;

const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

const objectivePin = (objective) => {
  let geo = objective?.objectiveExtraData?.geolocation;
  if (Array.isArray(geo)) geo = geo[0];
  const lat = Number(geo?.lat);
  const lng = Number(geo?.lng);
  if (!geo || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    return null;
  }
  return { lat, lng };
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    // Cache-bust: the visible <img> tags cached a header-less response that
    // Chromium would otherwise reuse for this CORS request and then block.
    img.src = `${src}${src.includes('?') ? '&' : '?'}cors=1`;
  });

const drawCover = (ctx, img, x, y, w, h) => {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
};

// Collage slot rectangles for 1..N photos inside the given area, with thin
// white gutters — printed-photo-collage look.
const collageSlots = (count, w, h, gap) => {
  if (count <= 1) return [{ x: 0, y: 0, w, h }];
  if (count === 2) {
    const rowH = (h - gap) / 2;
    return [
      { x: 0, y: 0, w, h: rowH },
      { x: 0, y: rowH + gap, w, h: rowH },
    ];
  }
  if (count === 3) {
    const topH = Math.round(h * 0.58);
    const botH = h - topH - gap;
    const colW = (w - gap) / 2;
    return [
      { x: 0, y: 0, w, h: topH },
      { x: 0, y: topH + gap, w: colW, h: botH },
      { x: colW + gap, y: topH + gap, w: colW, h: botH },
    ];
  }
  if (count === 4) {
    const colW = (w - gap) / 2;
    const rowH = (h - gap) / 2;
    return [
      { x: 0, y: 0, w: colW, h: rowH },
      { x: colW + gap, y: 0, w: colW, h: rowH },
      { x: 0, y: rowH + gap, w: colW, h: rowH },
      { x: colW + gap, y: rowH + gap, w: colW, h: rowH },
    ];
  }
  // 5+: hero photo on top, three small below
  const topH = Math.round(h * 0.62);
  const botH = h - topH - gap;
  const colW = (w - gap * 2) / 3;
  return [
    { x: 0, y: 0, w, h: topH },
    { x: 0, y: topH + gap, w: colW, h: botH },
    { x: colW + gap, y: topH + gap, w: colW, h: botH },
    { x: colW * 2 + gap * 2, y: topH + gap, w: colW, h: botH },
  ];
};

const TourRecapHero = ({ project, images = [] }) => {
  const [sharing, setSharing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const blobRef = useRef(null);
  const queryClient = useQueryClient();

  const isPublished = project?.isPublic === true;
  const shareUrl = project?.id ? buildCanonicalProjectShareUrl(project.id) : '';

  // Publishing exposes the whole run (photos included) — only ever called
  // from the explicit confirm inside the share panel, never one-tap.
  const publishMutation = useMutation({
    mutationFn: () => projectAPI.update(project.id, { isPublic: true }),
    onSuccess: async () => {
      queryClient.invalidateQueries(createQueryKey.project(project.id));
      queryClient.invalidateQueries(['user-projects']);
      try {
        await copyShareLink();
      } catch {
        /* copy is best-effort; the panel still shows the link */
      }
    },
    onError: () => toast.error('Could not publish the tour'),
  });

  const copyShareLink = async () => {
    await copyTextToClipboard(
      buildProjectShareClipboardText({ projectName: project?.name, url: shareUrl })
    );
    toast.success('Link copied');
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: project?.name || 'My tour', url: shareUrl });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        // fall through to clipboard
      }
    }
    try {
      await copyShareLink();
    } catch {
      toast.error('Could not copy the link');
    }
  };

  // Same query key as VenueBacklink → cache hit, no extra request
  const { data: partnerResp } = useQuery({
    queryKey: ['partner-by-starter', project?.originalProjectId],
    queryFn: () => partnerAPI.getByStarter(project.originalProjectId),
    enabled: Boolean(project?.originalProjectId),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const partner = partnerResp?.data?.data?.partner;

  const objectives = useMemo(
    () =>
      [...(project?.objectives || [])].sort(
        (a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)
      ),
    [project?.objectives]
  );

  // The recap also shows for explicitly ENDED partial runs (Aug 3): stats,
  // route and date must describe what the visitor actually DID — the
  // completed steps — never the full plan they skipped part of.
  const completedObjectives = useMemo(
    () => objectives.filter((o) => o.isCompleted === true),
    [objectives]
  );
  const allDone =
    objectives.length > 0 && completedObjectives.length === objectives.length;

  const pins = useMemo(
    () => completedObjectives.map(objectivePin).filter(Boolean),
    [completedObjectives]
  );

  const stats = useMemo(() => {
    const stops = completedObjectives.length;
    let walkKm = null;
    let minutes = null;
    if (pins.length >= 2) {
      walkKm = 0;
      for (let i = 1; i < pins.length; i++) walkKm += haversineKm(pins[i - 1], pins[i]);
      walkKm *= ROUTE_DETOUR_FACTOR;
      minutes =
        Math.round(((walkKm * 1000) / WALK_SPEED_M_PER_MIN + stops * DWELL_MINUTES_PER_STOP) / 5) * 5;
    }
    return { stops, total: objectives.length, walkKm, minutes };
  }, [completedObjectives, objectives.length, pins]);

  // Real completion date: when the last objective was checked off
  const completedDate = useMemo(() => {
    const times = completedObjectives
      .map((o) => (o.completedAt ? new Date(o.completedAt).getTime() : null))
      .filter((t) => Number.isFinite(t));
    const ts = times.length ? Math.max(...times) : Date.now();
    return new Date(ts);
  }, [completedObjectives]);

  // Strictly the visitor's own uploads (source field is authoritative;
  // takenBy is a user object even for AI images and never distinguishes them)
  const userPhotos = useMemo(
    () =>
      images.filter((img) => {
        if (typeof img === 'string') return true;
        if (img?.source) return img.source === 'user_upload';
        return true;
      }),
    [images]
  );

  // Collage falls back to AI guide images only when the visitor took none —
  // a photo-less card would be worse than an illustrated one.
  const collagePhotos = userPhotos.length > 0 ? userPhotos : images;

  const statLine = useMemo(() => {
    const stopsPart = allDone
      ? `${stats.stops} ${stats.stops === 1 ? 'stop' : 'stops'}`
      : `${stats.stops} of ${stats.total} stops`;
    const parts = [stopsPart];
    if (stats.walkKm != null) {
      parts.push(stats.walkKm >= 1 ? `${stats.walkKm.toFixed(1)} km` : `${Math.round(stats.walkKm * 1000)} m`);
    }
    if (stats.minutes != null) parts.push(`~${stats.minutes} min`);
    return parts.join(' · ');
  }, [stats, allDone]);

  const buildPostcard = async () => {
    const W = 1080;
    const H = 1350;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    // White base = the gutters between collage photos
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // ---- Photo collage fills everything above the caption band ----
    const bandH = 170;
    const collageH = H - bandH;
    const gap = 10;

    // Load up to 4 of their photos in parallel; drop the ones that fail
    const candidates = collagePhotos.slice(0, 4).map((img) =>
      getImageUrl(typeof img === 'string' ? img : img.url)
    );
    const loaded = (
      await Promise.all(
        candidates.map((url) => loadImage(url).catch(() => null))
      )
    ).filter(Boolean);

    if (loaded.length === 0) {
      // No usable photos: quiet gradient so the card still works
      const bg = ctx.createLinearGradient(0, 0, 0, collageH);
      bg.addColorStop(0, '#059669');
      bg.addColorStop(1, '#134e4a');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, collageH);
    } else {
      const slots = collageSlots(loaded.length, W, collageH, gap);
      slots.forEach((slot, i) => {
        const img = loaded[Math.min(i, loaded.length - 1)];
        drawCover(ctx, img, slot.x, slot.y, slot.w, slot.h);
      });
    }

    // ---- Slim caption band ----
    const pad = 56;
    const bandY = collageH;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, bandY, W, bandH);

    // Title (single line, ellipsized) + stat line
    ctx.fillStyle = '#111827';
    ctx.font = '700 40px system-ui, -apple-system, sans-serif';
    ctx.textBaseline = 'alphabetic';
    let title = project?.name || 'My tour';
    const maxTitleW = W - pad * 2 - 220; // leave room for the footer logo/wordmark
    while (ctx.measureText(title).width > maxTitleW && title.length > 4) {
      title = `${title.slice(0, -2).trimEnd()}…`;
    }
    ctx.fillText(title, pad, bandY + 68);

    ctx.fillStyle = '#6b7280';
    ctx.font = '400 30px system-ui, -apple-system, sans-serif';
    const dateStr = completedDate.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    ctx.fillText(`${statLine} · ${dateStr}`, pad, bandY + 118);

    // Right side: venue logo (round) + okio wordmark, vertically centered
    let rx = W - pad;
    ctx.textAlign = 'right';
    ctx.fillStyle = '#9ca3af';
    ctx.font = '600 28px system-ui, -apple-system, sans-serif';
    ctx.fillText('okio.ai', rx, bandY + 118);
    ctx.textAlign = 'left';
    if (partner?.logoUrl) {
      try {
        const logo = await loadImage(getImageUrl(partner.logoUrl));
        const size = 64;
        const ly = bandY + 40;
        const lx = W - pad - size;
        ctx.save();
        ctx.beginPath();
        ctx.arc(lx + size / 2, ly + size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.1)';
        ctx.stroke();
        ctx.clip();
        drawCover(ctx, logo, lx, ly, size, size);
        ctx.restore();
      } catch {
        /* logo is decoration */
      }
    }

    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  };

  // Build once for the inline preview; the share button reuses the blob.
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        const blob = await buildPostcard();
        if (!alive || !blob) return;
        blobRef.current = blob;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (err) {
        console.error('[TourRecapHero] Postcard preview failed:', err);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, collagePhotos.length, partner?.slug]);

  const sharePostcard = async () => {
    setSharing(true);
    try {
      const blob = blobRef.current || (await buildPostcard());
      if (!blob) throw new Error('canvas export failed');
      const file = new File([blob], 'okio-tour-postcard.jpg', { type: 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: project?.name || 'My tour',
          text: `${allDone ? 'Completed: ' : ''}${project?.name || 'a tour'} — ${statLine}`,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'okio-tour-postcard.jpg';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success('Postcard downloaded');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('[TourRecapHero] Postcard share failed:', err);
        toast.error('Could not create the postcard');
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <section className="rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white shadow-lg">
      <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
        {/* Postcard preview — the souvenir itself, not a duplicate photo strip */}
        {previewUrl && (
          <button
            onClick={sharePostcard}
            className="flex-shrink-0 rotate-[-2deg] hover:rotate-0 transition-transform focus:outline-none"
            title="Share postcard"
          >
            <img
              src={previewUrl}
              alt="Your tour postcard"
              className="w-36 sm:w-44 rounded-lg shadow-2xl ring-4 ring-white/25"
            />
          </button>
        )}

        <div className="min-w-0 flex-1 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-2">
            <Trophy size={14} />
            {allDone ? 'Tour completed' : 'Tour ended'}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">{project?.name}</h2>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 text-sm text-emerald-100/90 mb-5">
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} />
              {allDone
                ? `${stats.stops} ${stats.stops === 1 ? 'stop' : 'stops'}`
                : `${stats.stops} of ${stats.total} stops`}
            </span>
            {stats.walkKm != null && (
              <span className="inline-flex items-center gap-1.5">
                <Footprints size={14} />
                {stats.walkKm >= 1 ? `${stats.walkKm.toFixed(1)} km` : `${Math.round(stats.walkKm * 1000)} m`}
              </span>
            )}
            {stats.minutes != null && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} />~{stats.minutes} min
              </span>
            )}
            {userPhotos.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Camera size={14} />
                {userPhotos.length} {userPhotos.length === 1 ? 'photo' : 'photos'}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-emerald-800 text-sm font-semibold hover:bg-emerald-50 transition-colors"
            >
              <Share2 size={15} />
              Share tour
            </button>
            {partner && (
              <Link
                to={`/p/${partner.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition-colors"
              >
                {partner.logoUrl && (
                  <img
                    src={getImageUrl(partner.logoUrl)}
                    alt=""
                    className="w-5 h-5 rounded object-contain bg-white"
                  />
                )}
                More from {partner.name}
                <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Share panel: link first (the rich artifact), postcard second.
          Publishing is an explicit, honest step — never one-tap. */}
      {shareOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[60000] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
            onClick={() => setShareOpen(false)}
          >
            <div
              className="w-full sm:max-w-sm bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl p-5"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Share your tour
                </h3>
                <button
                  onClick={() => setShareOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {isPublished ? (
                <div className="space-y-2">
                  <button
                    onClick={shareLink}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                  >
                    <LinkIcon size={16} />
                    Share tour link
                  </button>
                  <button
                    onClick={() => copyShareLink().catch(() => toast.error('Could not copy the link'))}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-semibold transition-colors"
                  >
                    <Globe size={16} />
                    Copy link
                  </button>
                  <button
                    onClick={() => {
                      setShareOpen(false);
                      sharePostcard();
                    }}
                    disabled={sharing}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-semibold transition-colors disabled:opacity-60"
                  >
                    {sharing ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
                    Share postcard image
                  </button>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 pt-1">
                    Your tour page is public. You can unpublish it anytime further down
                    this page.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Your tour page is members-only right now. Sharing a link makes it
                    <span className="font-semibold"> public — photos included</span>. You
                    can unpublish anytime.
                  </p>
                  <button
                    onClick={() => publishMutation.mutate()}
                    disabled={publishMutation.isPending || publishMutation.isLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-60"
                  >
                    {publishMutation.isPending || publishMutation.isLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Globe size={16} />
                    )}
                    Make public & copy link
                  </button>
                  <button
                    onClick={() => {
                      setShareOpen(false);
                      sharePostcard();
                    }}
                    disabled={sharing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-semibold transition-colors disabled:opacity-60"
                  >
                    {sharing ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
                    Just share the postcard image
                  </button>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    The postcard is only a picture — it doesn't make anything public.
                  </p>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </section>
  );
};

export default TourRecapHero;
